// src/services/researchCalendarService.js
//
// Reads and writes the shared **Research** Google Calendar. That calendar stays
// the single source of truth for scheduling — staff put things on it directly
// that never came from RegX, and RegX shows all of it.
//
// TWO TRANSPORTS, selected by CALENDAR_PROXY_ENABLED:
//
//   proxy (preferred) — POST to the `researchCalendar` Cloud Function with the
//     user's Firebase ID token. The function verifies the token, checks the
//     caller is a member of the tenant/site, loads THAT SITE's calendar config
//     from Firestore, and talks to the calendar through an adapter. The Apps
//     Script URL never reaches the browser.
//
//   direct (legacy) — POST straight to the Apps Script web app at
//     REACT_APP_CALENDAR_API. CRA inlines REACT_APP_* into the JS bundle, so
//     that URL is readable by anyone who loads RegX, and the endpoint is
//     deployed "Anyone" with no auth: it can be used to list every event
//     (patient names are in the titles), create events, and delete them.
//     Kept only so the flag can be flipped back instantly during rollout.
//
// Both transports speak the SAME request/response shapes, so callers
// (ScheduleVisitModal, CalendarEventModal, ResearchCalendarPage) are unchanged.

import { auth } from "../firebase";
import { FUNCTIONS_BASE } from "../config/functionsBase";
import {
  getActiveTenantId,
  getActiveSiteId,
  activeSiteUsesLegacyCalendar,
} from "./activeTenant";
import { CALENDAR_PROXY_ENABLED, CALENDAR_SAMPLE_VISITS } from "../config/featureFlags";
import { SAMPLE_READ_ONLY_MESSAGE, sampleVisits } from "./sampleVisits";
import { fetchActiveCoordinators } from "./coordinatorService";

const FN_BASE = FUNCTIONS_BASE;
const apiURL = process.env.REACT_APP_CALENDAR_API;

// ---------------------------------------------------------------------------
// Transport: authenticated Cloud Function
// ---------------------------------------------------------------------------
async function callViaProxy(payload) {
  const user = auth.currentUser;
  if (!user) throw new Error("You must be signed in to use the calendar.");
  const idToken = await user.getIdToken();

  const resp = await fetch(`${FN_BASE}/researchCalendar`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${idToken}`,
    },
    body: JSON.stringify({
      tenantId: getActiveTenantId(),
      siteId: getActiveSiteId(),
      ...payload,
    }),
  });

  const data = await resp.json().catch(() => ({}));
  if (!resp.ok) {
    throw new Error(data.error || `Calendar request failed (${resp.status}).`);
  }
  if (data && data.error) throw new Error(data.error);
  return data;
}

// ---------------------------------------------------------------------------
// Transport: direct to the Apps Script web app (legacy)
// ---------------------------------------------------------------------------
async function callDirect(payload) {
  if (!apiURL) {
    throw new Error(
      "Research calendar is not configured (REACT_APP_CALENDAR_API is unset)."
    );
  }
  // IMPORTANT: use a "simple" content type (text/plain) so the browser does NOT
  // send a CORS preflight. Apps Script web apps can't answer the OPTIONS preflight,
  // so an application/json request is blocked by CORS. The script still reads the
  // raw body via e.postData.contents and JSON.parse's it regardless of content type.
  const res = await fetch(apiURL, {
    method: "POST",
    body: JSON.stringify(payload),
    headers: { "Content-Type": "text/plain;charset=utf-8" },
  });
  const data = await res.json();
  if (data && data.error) throw new Error(data.error);
  return data;
}

export const NOT_CONNECTED_MESSAGE =
  "No calendar is connected for this site yet. An admin can set one up in Manage → Calendar.";

const callCalendar = (payload) => {
  if (CALENDAR_PROXY_ENABLED) return callViaProxy(payload);

  // The direct path posts to a SINGLE Apps Script bound to ONE calendar — it
  // takes no site parameter and can't. Letting another site use it would show
  // that site Dubois's patients and drop its bookings onto Dubois's calendar.
  // The proxy path enforces this server-side (isLegacyCalendarSite); this is
  // the same guard for the legacy path, so the feature flag being OFF is not a
  // way around site isolation.
  if (!activeSiteUsesLegacyCalendar()) {
    // STAGING ONLY (CALENDAR_SAMPLE_VISITS): no calendar is connected, so show
    // made-up sample visits to look at; booking stays off. Only here, so it
    // can never stand in for a calendar that is connected.
    if (CALENDAR_SAMPLE_VISITS) {
      if (payload.action === "list") {
        return fetchActiveCoordinators()
          .catch(() => [])
          .then((coordinators) => ({ events: sampleVisits(payload.timeMin, payload.timeMax, coordinators) }));
      }
      return Promise.reject(new Error(SAMPLE_READ_ONLY_MESSAGE));
    }
    // Reads degrade to "nothing scheduled" so the page renders its
    // not-connected state; writes fail loudly rather than silently going
    // somewhere they don't belong.
    if (payload.action === "list") return Promise.resolve({ events: [] });
    return Promise.reject(new Error(NOT_CONNECTED_MESSAGE));
  }
  return callDirect(payload);
};

// ---------------------------------------------------------------------------
// Public API — unchanged shapes, so no caller had to be touched.
// ---------------------------------------------------------------------------

// Create or update a visit event. `payload` fields: eventId?, date, startTime,
// endTime, room, otherRoom, coordinatorName, coordinatorEmail, study, visit,
// notes, patientName, patientToken, createdBy. Returns { eventId, htmlLink }.
export const upsertCalendarEvent = (payload) =>
  callCalendar({ ...payload, action: "upsert" });

// Cancel a visit event. Returns { ok: true }.
export const deleteCalendarEvent = (eventId) =>
  callCalendar({ action: "delete", eventId });

// Read-only: list events in [timeMin, timeMax) (ISO datetime strings). Powers
// the Research Calendar view page — does not affect upsert/delete.
export const listCalendarEvents = async (timeMin, timeMax) => {
  const data = await callCalendar({ action: "list", timeMin, timeMax });
  return data.events || [];
};
